import React, { useEffect, useRef, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { X, RefreshCw, AlertCircle } from 'lucide-react';
import type { BackgroundPresetItem } from '../services/kieu';
import {
  getPresetSwatchStyle,
  groupBackgroundPresets,
  getRecentBackgroundPresetIds,
  recordRecentBackgroundPresetId,
} from '../services/backgroundPost';

export interface BackgroundPickerDialogProps {
  isOpen: boolean;
  onClose: () => void;
  backgroundId: string | null;
  onSelectBackground: (id: string | null) => void;
  presets: readonly BackgroundPresetItem[] | null;
  isLoading?: boolean;
  error?: string | null;
  onRefresh?: () => void;
  recentPresetIds?: readonly string[];
  tenantId?: string | null;
  actorId?: string | null;
}

/**
 * Centered modal dialog for choosing Facebook group post backgrounds.
 *
 * Implements accessible modal patterns (role="dialog", focus trap, focus restoration,
 * Escape key, backdrop dismiss, no-submit dismissal) matching Facebook dark charcoal aesthetics.
 * Uses createPortal to document.body to escape composer stacking context and clipping.
 *
 * Preserves truth in metadata:
 * - Solid colors -> "Một màu trơn"
 * - Gradients -> "Màu chuyển sắc"
 * - Image backgrounds -> "Trang trí" (same-origin image preview with credentials)
 * - Null previews -> "Chưa có hình xem trước" (honest neutral markers, no fabricated imagery)
 * - Real usage -> "Dùng gần đây" (strictly real recent usage history scoped by tenant+actor, never fabricated popularity)
 */
export const BackgroundPickerDialog: React.FC<BackgroundPickerDialogProps> = ({
  isOpen,
  onClose,
  backgroundId,
  onSelectBackground,
  presets,
  isLoading = false,
  error = null,
  onRefresh,
  recentPresetIds,
  tenantId,
  actorId,
}) => {
  const dialogRef = useRef<HTMLDivElement>(null);
  const previousActiveElementRef = useRef<HTMLElement | null>(null);

  // Keep latest callbacks in refs so unstable references never trigger focus trap re-runs
  const onCloseRef = useRef(onClose);
  useEffect(() => {
    onCloseRef.current = onClose;
  });

  const onSelectBackgroundRef = useRef(onSelectBackground);
  useEffect(() => {
    onSelectBackgroundRef.current = onSelectBackground;
  });

  // Group presets based on authoritative metadata and tenant+actor scoped recent usage
  const effectiveRecentIds = useMemo(() => {
    if (Array.isArray(recentPresetIds)) {
      return recentPresetIds;
    }
    return getRecentBackgroundPresetIds(tenantId, actorId);
  }, [recentPresetIds, tenantId, actorId, isOpen]);

  const grouped = useMemo(() => {
    return groupBackgroundPresets(presets, effectiveRecentIds);
  }, [presets, effectiveRecentIds]);

  // Manage focus trap and focus restoration.
  // CRITICAL: Effect depends ONLY on isOpen. Parent re-renders with unstable onClose
  // will NEVER re-trigger this effect or reset focus back to the top!
  useEffect(() => {
    if (!isOpen) return;

    previousActiveElementRef.current = document.activeElement as HTMLElement;

    const timer = window.setTimeout(() => {
      if (!dialogRef.current) return;
      const focusable = dialogRef.current.querySelectorAll<HTMLElement>(
        'button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])',
      );
      if (focusable.length > 0) {
        focusable[0].focus();
      } else {
        dialogRef.current.focus();
      }
    }, 30);

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.stopPropagation();
        onCloseRef.current();
        return;
      }

      if (e.key === 'Tab' && dialogRef.current) {
        const focusable = Array.from(
          dialogRef.current.querySelectorAll<HTMLElement>(
            'button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])',
          ),
        );
        if (focusable.length === 0) return;

        const first = focusable[0];
        const last = focusable[focusable.length - 1];

        if (e.shiftKey) {
          if (document.activeElement === first || !dialogRef.current.contains(document.activeElement)) {
            e.preventDefault();
            last.focus();
          }
        } else {
          if (document.activeElement === last || !dialogRef.current.contains(document.activeElement)) {
            e.preventDefault();
            first.focus();
          }
        }
      }
    };

    window.addEventListener('keydown', handleKeyDown, true);
    return () => {
      window.clearTimeout(timer);
      window.removeEventListener('keydown', handleKeyDown, true);
      if (previousActiveElementRef.current && typeof previousActiveElementRef.current.focus === 'function') {
        previousActiveElementRef.current.focus();
      }
    };
  }, [isOpen]);

  const currentSelectedPreset = useMemo(() => {
    if (!backgroundId || !presets) return null;
    return presets.find((p) => p.id === backgroundId) || null;
  }, [backgroundId, presets]);

  if (!isOpen) return null;

  const handleBackdropClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (e.target === e.currentTarget) {
      onCloseRef.current();
    }
  };

  const handleClear = () => {
    onSelectBackgroundRef.current(null);
  };

  const handleSelectPreset = (id: string) => {
    onSelectBackgroundRef.current(id);
    recordRecentBackgroundPresetId(id, tenantId, actorId);
  };

  // Section list matching authoritative metadata specifications
  const sections: { title: string; items: BackgroundPresetItem[] }[] = [];

  if (grouped.recent.length > 0) {
    sections.push({ title: 'Dùng gần đây', items: grouped.recent });
  }
  if (grouped.decorative.length > 0) {
    sections.push({ title: 'Trang trí', items: grouped.decorative });
  }
  if (grouped.gradient.length > 0) {
    sections.push({ title: 'Màu chuyển sắc', items: grouped.gradient });
  }
  if (grouped.solid.length > 0) {
    sections.push({ title: 'Một màu trơn', items: grouped.solid });
  }
  if (grouped.unpreviewed.length > 0) {
    sections.push({ title: 'Chưa có hình xem trước', items: grouped.unpreviewed });
  }

  const renderSwatch = (preset: BackgroundPresetItem, sectionKey: string) => {
    const isSelected = backgroundId === preset.id;
    const swatchStyle = getPresetSwatchStyle(preset.preview);
    const hasPreview = Boolean(preset.preview);
    const isImage = preset.preview?.type === 'image';

    const tooltip = hasPreview
      ? `${preset.label} (${preset.id})`
      : `${preset.label} (${preset.id}) — Chưa có hình xem trước từ Facebook`;

    const accessibleName = `Phông nền ${preset.label} (preset ${preset.id})`;

    return (
      <button
        key={`${sectionKey}-${preset.id}`}
        type="button"
        role="button"
        aria-label={accessibleName}
        aria-pressed={isSelected}
        title={tooltip}
        onClick={() => handleSelectPreset(preset.id)}
        className={`group aspect-square w-full rounded-[6px] relative flex flex-col items-center justify-center transition-all duration-150 focus:outline-none focus:ring-2 focus:ring-blue-400 select-none overflow-hidden ${
          isSelected
            ? 'border-2 border-dashed border-blue-400 ring-2 ring-blue-500/50 scale-[1.03] z-10 shadow-lg'
            : 'border border-white/10 hover:border-white/30 hover:scale-[1.02]'
        }`}
        style={swatchStyle}
      >
        {isImage && preset.preview?.assetUrl && (
          <img
            src={preset.preview.assetUrl}
            alt=""
            aria-hidden="true"
            loading="eager"
            crossOrigin="use-credentials"
            className="w-full h-full object-cover"
            onError={(e) => {
              // Honest null/fallback: if thumbnail fails to load, hide img element so fallbackColor shows
              (e.currentTarget as HTMLElement).style.display = 'none';
            }}
          />
        )}
        {!hasPreview && (
          <div className="w-full h-full bg-[#3a3b3c] group-hover:bg-[#4a4b4c] text-slate-200 flex flex-col items-center justify-center p-1 text-center">
            <span className="text-[11px] font-semibold text-slate-200 leading-tight truncate max-w-full px-0.5">
              {preset.label || preset.id}
            </span>
            <span className="text-[10px] text-slate-300 font-mono mt-0.5 leading-none">∅</span>
          </div>
        )}
      </button>
    );
  };

  const modalNode = (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-3 sm:p-4 bg-black/70 backdrop-blur-sm animate-fade-in"
      onClick={handleBackdropClick}
      role="presentation"
    >
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby="background-picker-title"
        tabIndex={-1}
        className="relative w-full max-w-[480px] max-h-[85vh] flex flex-col rounded-2xl bg-[#242526] text-slate-100 shadow-2xl border border-slate-700/60 overflow-hidden outline-none animate-scale-in"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-4 py-3.5 border-b border-slate-700/60 shrink-0">
          <button
            type="button"
            onClick={handleClear}
            className="text-sm font-semibold text-blue-400 hover:text-blue-300 transition focus:outline-none focus:ring-2 focus:ring-blue-500/50 rounded px-1.5 py-0.5"
            aria-label="Xóa phông nền"
          >
            Xóa
          </button>
          <h2 id="background-picker-title" className="text-base font-bold text-center text-white flex-1 truncate px-2">
            Phông nền
          </h2>
          <button
            type="button"
            onClick={() => onCloseRef.current()}
            aria-label="Đóng"
            className="flex items-center justify-center w-8 h-8 rounded-full bg-[#3a3b3c] hover:bg-[#4e4f50] text-slate-300 hover:text-white transition focus:outline-none focus:ring-2 focus:ring-blue-500/50"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Selected Preset Live Preview Bar */}
        <div className="px-4 py-2.5 bg-[#1c1d1e] border-b border-slate-700/60 flex items-center justify-between gap-3 shrink-0">
          <div className="flex items-center gap-2.5 min-w-0 flex-1">
            <div
              className="w-7 h-7 rounded-[5px] border border-white/20 shrink-0 overflow-hidden flex items-center justify-center relative shadow-sm"
              style={currentSelectedPreset ? getPresetSwatchStyle(currentSelectedPreset.preview) : undefined}
            >
              {currentSelectedPreset?.preview?.type === 'image' && currentSelectedPreset.preview.assetUrl ? (
                <img
                  src={currentSelectedPreset.preview.assetUrl}
                  alt=""
                  aria-hidden="true"
                  className="w-full h-full object-cover"
                />
              ) : !currentSelectedPreset ? (
                <span className="text-slate-500 text-xs font-mono">—</span>
              ) : currentSelectedPreset.preview?.type === 'solid' || currentSelectedPreset.preview?.type === 'gradient' ? null : (
                <span className="text-slate-400 text-[10px] font-mono">∅</span>
              )}
            </div>
            <div className="min-w-0 flex-1">
              <div className="text-[10px] text-slate-400 uppercase tracking-wider font-semibold">Đang chọn</div>
              <div className="text-xs font-semibold text-white truncate">
                {currentSelectedPreset ? (
                  <>
                    <span>{currentSelectedPreset.label}</span>
                    <span className="text-slate-400 font-normal ml-1 font-mono text-[11px]">({currentSelectedPreset.id})</span>
                  </>
                ) : backgroundId ? (
                  <span className="font-mono text-slate-200">{backgroundId}</span>
                ) : (
                  <span className="text-slate-400 font-normal italic">Mặc định (không phông nền)</span>
                )}
              </div>
            </div>
          </div>
          {backgroundId && (
            <button
              type="button"
              onClick={handleClear}
              className="text-[11px] font-medium text-slate-400 hover:text-red-400 px-2 py-0.5 rounded bg-[#2a2b2c] hover:bg-[#3a3b3c] transition shrink-0"
              aria-label="Bỏ chọn phông nền"
            >
              Bỏ chọn
            </button>
          )}
        </div>

        {/* Scrollable Content Body */}
        <div className="flex-1 overflow-y-auto p-4 space-y-5">
          {/* Loading Indicator */}
          {isLoading && (!presets || presets.length === 0) && (
            <div className="py-12 flex flex-col items-center justify-center gap-2 text-slate-400 text-xs">
              <RefreshCw className="w-5 h-5 animate-spin text-blue-400" />
              <span>Đang tải danh mục phông nền...</span>
            </div>
          )}

          {/* Error Message */}
          {error && (!presets || presets.length === 0) && (
            <div className="p-3.5 rounded-lg bg-red-950/40 border border-red-800/50 text-red-200 text-xs flex flex-col items-center gap-2">
              <div className="flex items-center gap-1.5">
                <AlertCircle className="w-4 h-4 text-red-400 shrink-0" />
                <span>{error}</span>
              </div>
              {onRefresh && (
                <button
                  type="button"
                  onClick={onRefresh}
                  className="mt-1 px-3 py-1 bg-red-800/60 hover:bg-red-700/60 rounded text-white font-medium transition"
                >
                  Thử lại
                </button>
              )}
            </div>
          )}

          {/* Grouped Swatches */}
          {sections.map((section) => (
            <div key={section.title} className="space-y-2">
              <div className="flex items-center justify-between">
                <h3 className="text-xs font-semibold text-slate-200 uppercase tracking-wider">
                  {section.title}
                </h3>
                <span className="text-[11px] text-slate-300">({section.items.length})</span>
              </div>
              <div className="grid grid-cols-6 sm:grid-cols-7 gap-[6px]">
                {section.items.map((preset) => renderSwatch(preset, section.title))}
              </div>
            </div>
          ))}

          {/* Empty State */}
          {!isLoading && !error && presets && presets.length === 0 && (
            <div className="py-8 text-center text-xs text-slate-400">
              Không có phông nền nào khả dụng.
            </div>
          )}

          {/* Disabled Presets Note */}
          {grouped.disabled.length > 0 && (
            <div className="pt-2 border-t border-slate-700/60 text-[11px] text-slate-400 flex items-center justify-between">
              <span>Chưa kích hoạt ({grouped.disabled.length})</span>
              <span className="text-[10px] text-slate-400">Chỉ hiện phông nền đã kích hoạt</span>
            </div>
          )}
        </div>

        {/* Footer with Manual Refresh and Xong button */}
        <div className="px-4 py-2.5 bg-[#1e1f20] border-t border-slate-700/60 flex items-center justify-between text-xs text-slate-300 shrink-0">
          <div className="flex items-center gap-3">
            <span>{presets ? `${presets.filter((p) => p.enabled).length} khả dụng` : '0 khả dụng'}</span>
            {onRefresh && (
              <button
                type="button"
                onClick={onRefresh}
                disabled={isLoading}
                title="Tải lại danh mục phông nền từ máy chủ"
                className="inline-flex items-center gap-1.5 text-xs text-slate-300 hover:text-white disabled:opacity-50 transition"
              >
                <RefreshCw className={`w-3.5 h-3.5 ${isLoading ? 'animate-spin' : ''}`} />
                <span>Tải lại</span>
              </button>
            )}
          </div>
          <button
            type="button"
            onClick={() => onCloseRef.current()}
            className="px-4 py-1.5 bg-blue-600 hover:bg-blue-500 active:bg-blue-700 text-white font-semibold text-xs rounded-lg transition shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-400"
            aria-label="Xong — Hoàn tất chọn phông nền"
          >
            Xong
          </button>
        </div>
      </div>
    </div>
  );

  // createPortal to document.body to escape composer stacking context and clipping
  if (typeof document !== 'undefined' && document.body) {
    return createPortal(modalNode, document.body);
  }
  return modalNode;
};
